/**
 * Split fields a policy hides in the request/rendition creation and edit forms. Display-only: a hidden
 * field keeps its default value, so `Splits` gets the full custom-field list plus the hidden ids.
 */

export interface SplitFieldVisibility {
  hideCostCenter: boolean
  hideAccount: boolean
  hiddenCustomFieldIds: number[]
}

export interface RawSplitFieldVisibility {
  hide_cost_center?: boolean
  hide_account?: boolean
  hidden_custom_field_ids?: number[]
}

export const SHOW_ALL_SPLIT_FIELDS: Readonly<SplitFieldVisibility> = Object.freeze({
  hideCostCenter: false,
  hideAccount: false,
  hiddenCustomFieldIds: [],
})

export function parseSplitFieldVisibility(raw?: RawSplitFieldVisibility | null): SplitFieldVisibility {
  return {
    hideCostCenter: raw?.hide_cost_center ?? false,
    hideAccount: raw?.hide_account ?? false,
    hiddenCustomFieldIds: raw?.hidden_custom_field_ids ?? [],
  }
}

// Multiselect encoding for the policy form: one value per field the requester will SEE.
export const SPLIT_FIELD_COST_CENTER = 'cost_center'
export const SPLIT_FIELD_ACCOUNT = 'account'

export function customFieldSelectionValue(id: number): string {
  return `custom_field:${id}`
}

export function toVisibleSelection(visibility: SplitFieldVisibility, customFieldIds: number[]): string[] {
  const hidden = new Set(visibility.hiddenCustomFieldIds)
  return [
    ...(visibility.hideCostCenter ? [] : [SPLIT_FIELD_COST_CENTER]),
    ...(visibility.hideAccount ? [] : [SPLIT_FIELD_ACCOUNT]),
    ...customFieldIds.filter((id) => !hidden.has(id)).map(customFieldSelectionValue),
  ]
}

// Cost center and account travel inside the policy's field_visibility; custom fields go on their own.
export function fromVisibleSelection(selection: string[], customFieldIds: number[]): {
  fieldVisibility: { split_cost_center: boolean; split_account: boolean }
  hidden_split_custom_fields: number[]
} {
  const visible = new Set(selection)
  return {
    fieldVisibility: {
      split_cost_center: visible.has(SPLIT_FIELD_COST_CENTER),
      split_account: visible.has(SPLIT_FIELD_ACCOUNT),
    },
    hidden_split_custom_fields: customFieldIds.filter((id) => !visible.has(customFieldSelectionValue(id))),
  }
}

// Same rule the backend enforces on save: a split needs a cost center or an account, and the
// per-line distribution needs the cost center on every row.
export function lockedSplitFields(selection: string[], options: { lineSplits: boolean }): string[] {
  const locked = new Set<string>()
  if (options.lineSplits || !selection.includes(SPLIT_FIELD_ACCOUNT)) locked.add(SPLIT_FIELD_COST_CENTER)
  if (!selection.includes(SPLIT_FIELD_COST_CENTER)) locked.add(SPLIT_FIELD_ACCOUNT)
  return [...locked]
}

export function withLockedSplitFields(selection: string[], locked: string[], order: string[]): string[] {
  const next = new Set([...selection, ...locked])
  return order.filter((value) => next.has(value))
}

export function splitFieldVisibilityFor(policyVisibility: SplitFieldVisibility, options: { reviewMode: boolean }): SplitFieldVisibility {
  return options.reviewMode ? SHOW_ALL_SPLIT_FIELDS : policyVisibility
}
